import { BootResponse, DeviceType } from '@appcharge/shared-ui';
import { processRichTextContent } from 'utils/colorUtils';
import { z } from 'zod';

import { DEFAULT_LANGUAGE } from 'components/LanguagePicker/constants';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';

import { templateStudioSchema } from '../../validation-schema';

const _generateGeneral = (
  formValues: z.infer<typeof templateStudioSchema>,
  general: BootResponse['storeTheme']['general']
): BootResponse['storeTheme']['general'] => {
  return {
    ...general,
    font: formValues.brandKit.fontFamily,
    backgroundImageDesktop:
      formValues.brandKit.backgroundImageDesktop ||
      'https://media.appcharge.com/defaults/template-studio/default_preview_background.jpg',
    backgroundImageMobile:
      formValues.brandKit.backgroundImageMobile ||
      'https://media.appcharge.com/defaults/template-studio/default_preview_background.jpg',
    logo: formValues.brandKit.logo,
    specialOffersInternalViewModel: formValues.promotion.selectedDesignId,
    bundlesViewModel: formValues.bundle.bundleViewModel,
    bundlesInternalViewModel: formValues.bundle.selectedDesignId,
    rollingOffersInternalViewModel: formValues.rollingOffers.selectedDesignId,
    progressBarInternalViewModel: formValues.progressBar.selectedDesignId,
    dailyBonusBadgeInternalViewModel:
      formValues.dailyBonusBadge.selectedDesignId,
    dailyBonusInternalViewModel: formValues.popup.selectedDesignId,
    triggeredPopupsInternalViewModel:
      formValues.triggeredPopups?.selectedDesignId,
    triggeredPopupsBadgeInternalViewModel:
      formValues.triggeredPopups?.selectedBadgeDesignId,
    footer: {
      ...general.footer,
      showAppchargeLogo: formValues.footer.showAppchargeLogo,
      opacity: formValues.footer.backgroundOpacity,
      textColor: formValues.footer.textColor,
      imageMobile: formValues.brandKit.footerMobileImage,
      imageDesktop: formValues.brandKit.footerDesktopImage
    }
  };
};

const _generateStoreScreen = (
  formValues: z.infer<typeof templateStudioSchema>,
  storeScreen: BootResponse['storeTheme']['storeScreen'],
  previewDeviceLayout: DeviceType
): BootResponse['storeTheme']['storeScreen'] => {
  const addToHomeScreen = formValues.externalButtons.addToHomeScreenButton
    ?.active
    ? {
        borderColor:
          formValues.externalButtons.addToHomeScreenButton.borderColor,
        backgroundColor:
          formValues.externalButtons.addToHomeScreenButton.backgroundColor,
        active: formValues.externalButtons.addToHomeScreenButton.active,
        buttonImage:
          formValues.externalButtons.addToHomeScreenButton.buttonImage,
        iconImage: formValues.externalButtons.addToHomeScreenButton.iconImage,
        shortcutName:
          formValues.externalButtons.addToHomeScreenButton.shortcutName,
        verticalOffset: formValues.externalButtons.verticalOffset
      }
    : { verticalOffset: formValues.externalButtons.verticalOffset ?? 0 };

  const backToGameButton = formValues.externalButtons.backToGameButton?.active
    ? {
        active: formValues.externalButtons.backToGameButton.active,
        buttonImage: formValues.externalButtons.backToGameButton.buttonImage,
        position: formValues.externalButtons.backToGameButton.position
      }
    : undefined;

  return {
    ...storeScreen,
    buttonColor: formValues.brandKit.buttonColor,
    buttonTextColor: formValues.brandKit.buttonTextColor,
    rollingOffersDesktopLayout:
      previewDeviceLayout === DeviceType.MOBILE
        ? ETemplateStudioLayoutOptions.VERTICAL
        : formValues.rollingOffers.desktopLayout,
    header: {
      ...storeScreen.header,
      showLogo: formValues.header.showLogo,
      showPlayerName: formValues.header.showPlayerName,
      showPlayerId: formValues.header.showPlayerId,
      isSticky: formValues.header.isSticky,
      backgroundOpacity: formValues.header.backgroundOpacity,
      textColor: formValues.header.textColor?.colorOne
    },
    richText: processRichTextContent(formValues.richText),
    defaultBanner: formValues.brandKit.defaultBanner,
    addToHomeScreen,
    backToGameButton
  };
};

const _generateStoreTemplate = (
  formValues: z.infer<typeof templateStudioSchema>,
  storeTemplate: BootResponse['storeTheme']['storeTemplate']
): BootResponse['storeTheme']['storeTemplate'] => {
  return {
    ...storeTemplate,
    bodyBlocksOrder: formValues.bodyBlocksOrder
  };
};

export const generateBootInfo = (
  formValues: z.infer<typeof templateStudioSchema>,
  storeTheme: BootResponse['storeTheme'],
  previewDeviceLayout: DeviceType,
  realFeatureFlags?: Record<string, boolean>
): BootResponse => {
  return {
    externalServicesConfig: {},
    integration: {},
    currencyCode: 'USD',
    language: DEFAULT_LANGUAGE.value,
    storeTheme: {
      publisherId: storeTheme.publisherId,
      general: _generateGeneral(formValues, storeTheme.general),
      storeScreen: _generateStoreScreen(
        formValues,
        storeTheme.storeScreen,
        previewDeviceLayout
      ),
      storeTemplate: _generateStoreTemplate(
        formValues,
        storeTheme.storeTemplate
      ),
      login: {},
      paymentScreen: {},
      completedScreen: {}
    },
    supportConfiguration: {},
    featureFlags: {
      ...realFeatureFlags,
      store_multi_language_support: true,
      store_collect_animation_post_free_collect_promotions_popups: true,
      integration_profiles: true,
      store_present_daily_bonus_x_icon: true,
      store_animation_after_purchases: true,
      store_under_maintenance: false,
      store_approval_modal_external_browser: false,
      store_checkout_link_block_success_page: false,
      store_sse_support: true,
      store_successScreen_add_BackToStore_CTA: false,
      dashboard_new_privacy_rich_text: true,
      store_deeplinks_disable_store: false,
      store_remove_back_to_game_button_after_free: false,
      store_success_screen_hide_close_until_refetch: true,
      store_hide_mobile_background_overlay: false,
      store_send_os_authlogin: false,
      checkout_links_use_dynamo_jwt: false
    }
  };
};
